// src/components/common/ActivityLogsTab.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Activity,
    Clock,
    Monitor,
    Smartphone,
    Server,
    CheckCircle,
    XCircle,
    Calendar,
    Filter,
    Download,
    RefreshCw,
    Globe
} from 'lucide-react';
import attendanceService from '../../services/attendance.service';
import { formatISTDateTime, getISTDateFromUTC } from '../../utils/dateHelpers';

const ActivityLogsTab = ({ employeeId, employeeName, initialDate = null }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [fromDate, setFromDate] = useState(() => {
        if (initialDate) return initialDate;
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    });
    const [toDate, setToDate] = useState(() => {
        if (initialDate) return initialDate;
        return new Date().toISOString().split('T')[0];
    });
    const [eventTypeFilter, setEventTypeFilter] = useState('all');
    const [successFilter, setSuccessFilter] = useState('all');

    const fetchLogs = useCallback(async () => {
        if (!employeeId) return;

        setLoading(true);
        setError(null);

        try {
            const filters = {
                fromDate,
                toDate,
                eventType: eventTypeFilter !== 'all' ? eventTypeFilter : undefined,
                success: successFilter !== 'all' ? successFilter : undefined
            };

            const response = await attendanceService.getActivityLogs(employeeId, filters);
            setLogs(response.logs || []);
        } catch (err) {
            console.error('Error fetching activity logs:', err);
            console.error('Error details:', err.response?.data || err.message);
            // Show more specific error message
            const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to fetch activity logs';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [employeeId, fromDate, toDate, eventTypeFilter, successFilter]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // Format timestamp for display using IST
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        return formatISTDateTime(timestamp, { dateFormat: 'short', timeFormat: '12h' });
    };

    // Get event type badge
    const getEventBadge = (eventType) => {
        const badges = {
            app_load: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', label: 'App Load', icon: Monitor },
            punch_attempt: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', label: 'Punch Attempt', icon: Clock },
            regularization_submit: { color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300', label: 'Regularization', icon: Calendar },
            page_view: { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300', label: 'Page View', icon: Globe }
        };

        const badge = badges[eventType] || { color: 'bg-gray-100 text-gray-600', label: eventType, icon: Activity };
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon className="w-3 h-3" />
                {badge.label}
            </span>
        );
    };

    // Get success/failure badge
    const getStatusBadge = (success, failureReason) => {
        if (success === null || success === undefined) return null;

        if (success) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                    <CheckCircle className="w-3 h-3" />
                    Success
                </span>
            );
        }

        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" title={failureReason}>
                <XCircle className="w-3 h-3" />
                Failed
            </span>
        );
    };

    // Get device icon
    const getDeviceIcon = (deviceType) => {
        switch (deviceType) {
            case 'mobile':
                return <Smartphone className="w-4 h-4 text-gray-500" />;
            case 'biometric':
                return <Server className="w-4 h-4 text-gray-500" />;
            default:
                return <Monitor className="w-4 h-4 text-gray-500" />;
        }
    };

    // Get details for the log entry
    const getLogDetails = (log) => {
        const metadata = log.metadata ? (typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata) : {};

        switch (log.event_type) {
            case 'app_load':
                return metadata.page || 'Opened attendance page';
            case 'punch_attempt':
                const punchLabel = log.punch_type === 'check_in' ? 'Check-in' : 'Check-out';
                if (log.success === false) {
                    return `${punchLabel} - Failed (${log.failure_reason || 'Unknown error'})`;
                }
                return `${punchLabel} - ${log.success ? 'Success' : 'Pending'}`;
            case 'regularization_submit':
                const times = [];
                if (metadata.requestedPunchIn) times.push(`In: ${metadata.requestedPunchIn}`);
                if (metadata.requestedPunchOut) times.push(`Out: ${metadata.requestedPunchOut}`);
                const dateStr = log.target_date ? ` for ${getISTDateFromUTC(log.target_date)}` : '';
                return `Requested ${times.join(', ')}${dateStr}`;
            case 'page_view':
                return metadata.page || 'Viewed page';
            default:
                return log.failure_reason || '-';
        }
    };

    // Export logs to CSV
    const exportToCSV = () => {
        if (logs.length === 0) return;

        const headers = ['Timestamp', 'Event Type', 'Details', 'Device', 'IP Address', 'Status'];
        const rows = logs.map(log => [
            formatTimestamp(log.event_timestamp),
            log.event_type,
            getLogDetails(log),
            log.device_type || 'web',
            log.ip_address || '-',
            log.success === true ? 'Success' : log.success === false ? 'Failed' : '-'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `activity_logs_${employeeName || employeeId}_${fromDate}_${toDate}.csv`;
        link.click();
    };

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-3">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* Date Range */}
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To Date</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    {/* Event Type Filter */}
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Event Type</label>
                        <select
                            value={eventTypeFilter}
                            onChange={(e) => setEventTypeFilter(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                            <option value="all">All Events</option>
                            <option value="app_load">App Load</option>
                            <option value="punch_attempt">Punch Attempt</option>
                            <option value="regularization_submit">Regularization</option>
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Status</label>
                        <select
                            value={successFilter}
                            onChange={(e) => setSuccessFilter(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                            <option value="all">All Status</option>
                            <option value="true">Success</option>
                            <option value="false">Failed</option>
                        </select>
                    </div>

                    {/* Actions */}
                    <div className="flex items-end gap-2">
                        <button
                            onClick={fetchLogs}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                        <button
                            onClick={exportToCSV}
                            disabled={logs.length === 0}
                            className="flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                        >
                            <Download className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Logs Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading activity logs...</span>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center py-12 text-red-500">
                        <XCircle className="w-5 h-5 mr-2" />
                        {error}
                    </div>
                ) : logs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                        <Activity className="w-12 h-12 mb-3 opacity-50" />
                        <p>No activity logs found for the selected filters</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Timestamp
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Event Type
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Details
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Device
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        IP Address
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {logs.map((log) => (
                                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                            {formatTimestamp(log.event_timestamp)}
                                        </td>
                                        <td className="px-4 py-3">
                                            {getEventBadge(log.event_type)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate" title={getLogDetails(log)}>
                                            {getLogDetails(log)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                {getDeviceIcon(log.device_type)}
                                                <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                                                    {log.device_type || 'web'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 font-mono">
                                            {log.ip_address || '-'}
                                        </td>
                                        <td className="px-4 py-3">
                                            {getStatusBadge(log.success, log.failure_reason)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Footer with count */}
                {!loading && logs.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Showing {logs.length} activity log{logs.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivityLogsTab;
